import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  containsHyphenInQuote,
  formatDollarFractionString,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { returnDecimalFormatting } from "@/lib/globalFormatterHelper";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import tradeDataStore from "@/store/tradeDataStore";
import { ITradeSettings } from "@/store/tradeSettingsStoreACTIONS";
import {
  determineReferencePriceFromSettings,
  shouldShowReferencePrice,
  getNotationForLeg,
} from "./referencePriceLegHelpers";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";

/**
 * Format value only (for copy/paste - NO notation symbols)
 *
 * @param esid - The execution strategy ID
 * @param tradeSettings - All trade settings from the store
 * @returns Formatted string with value and trailing space
 */
export function formatReferencePriceValue(
  esid: string,
  tradeSettings: ITradeSettings[],
): string {
  const value = determineReferencePriceFromSettings(esid, tradeSettings);

  if (value === null || value === 0) {
    return "";
  }

  // $fr: if the value is a fraction string, format with padding
  if (typeof value === "string" && containsHyphenInQuote(value)) {
    return `${formatDollarFractionString(value)} `;
  }

  return `${value} `;
}

/**
 * Format with notation for display
 *
 * @param esid - The execution strategy ID
 * @param tradeSettings - All trade settings from the store
 * @returns Formatted string with value for display
 */
export function formatReferencePriceWithNotation(
  esid: string,
  tradeSettings: ITradeSettings[],
): string {
  const value = determineReferencePriceFromSettings(esid, tradeSettings);

  if (value === null || value === 0) {
    return "";
  }

  // $fr: if the value is a fraction string, format without suffix
  if (typeof value === "string" && containsHyphenInQuote(value)) {
    return formatDollarFractionString(value);
  }

  return `${value}`;
}

/**
 * AG Grid valueFormatter for copy/paste functionality
 * This ensures copied values match what's displayed by accessing Zustand store
 * Logic matches ReferencePriceLegCellRenderer display logic
 */
export function referencePriceLegValueFormatter(params: any): string {
  if (!params.data) {
    return "";
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId ?? params.data?.id;

  if (!esid) {
    return "";
  }

  // Access Zustand stores directly (not via React hooks)
  const tradeSettings =
    tradeSettingsStore.getState().tradeSettingsData;
  const thisTradeSettings = tradeSettings.find((trade) => trade.esid === esid);

  const currentTradeData = tradeDataStore.getState().tradeData;
  const thisTrade = currentTradeData.find((trade) => trade.esid === esid);

  const isOutright = fullTradeData.strategyType === StrategyType.Outright;
  const currentStatus = thisTrade?.status || fullTradeData.status;

  if (!currentStatus) {
    return "";
  }

  // Check if we should show the reference price based on settings
  const showRef = shouldShowReferencePrice(
    thisTradeSettings?.settings,
    currentStatus,
    isOutright,
  );

  if (!showRef) {
    return "";
  }

  // Get display value - same logic as renderer
  let displayValue: number | string | null | undefined =
    params.value !== null && params.value !== undefined ? params.value : null;

  // For Outright trades, get referencePrice from legs (synced with settings)
  if (isOutright && thisTrade?.legs && thisTrade.legs.length > 0) {
    const matchingLeg = thisTrade.legs.find((leg) => leg.blockId === blockId);
    if (matchingLeg?.referencePrice !== undefined) {
      displayValue = matchingLeg.referencePrice;
    }
  }

  // Apply notation-based rounding and return value only (no notation) for copy/paste
  if (
    displayValue !== null &&
    displayValue !== undefined &&
    displayValue !== ""
  ) {
    const notation = getNotationForLeg(esid, blockId, fullTradeData);

    // $fr: if the value is a fraction string, return formatted fraction (no suffix)
    if (
      notation === "$fr" &&
      typeof displayValue === "string" &&
      containsHyphenInQuote(displayValue)
    ) {
      return `${formatDollarFractionString(displayValue)} `;
    }

    const numValue =
      typeof displayValue === "string" ? parseFloat(displayValue) : displayValue;
    if (!isNaN(numValue)) {
      const roundedValue = returnDecimalFormatting(numValue, notation, true);
      return roundedValue ? `${roundedValue} ` : "";
    }
  }

  return "";
}
